import { useEffect, useRef } from 'react';
import type { SkyMapResponse } from '../lib/planet9';

interface Props {
  data: SkyMapResponse | null;
  width?: number;
  height?: number;
}

/** Heatmap RA/Dec rendue sur canvas 2D. Projection équirectangulaire simple :
 *  X = RA (0 à droite, 360 à gauche pour faire face au ciel)
 *  Y = Dec (-90 en bas, +90 en haut) */
export default function SkyMap({ data, width = 720, height = 360 }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // fond sombre étoilé
    ctx.fillStyle = '#02030a';
    ctx.fillRect(0, 0, width, height);
    drawGrid(ctx, width, height);

    if (!data || data.tiles.length === 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = '13px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Aucune donnée — démarre le backend.', width / 2, height / 2);
      return;
    }

    // Tiles
    const tileSize = data.tile_size_deg;
    const tilePxW = (tileSize / 360) * width;
    const tilePxH = (tileSize / 180) * height;
    for (const t of data.tiles) {
      // RA va de 0 à 360 — on inverse pour que RA=0 soit à gauche (convention)
      const x = ((360 - t.ra_deg) / 360) * width - tilePxW / 2;
      const y = ((90 - t.dec_deg) / 180) * height - tilePxH / 2;
      const a = Math.min(1, t.density * 1.2 + 0.05);
      // gradient violet → blanc selon densité
      const r = Math.floor(120 + 135 * t.density);
      const g = Math.floor(80 + 80 * t.density);
      const b = Math.floor(255);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
      ctx.fillRect(x, y, tilePxW, tilePxH);
    }

    // Légende
    drawLegend(ctx, width, height);
  }, [data, width, height]);

  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      style={{
        width: '100%',
        maxWidth: width,
        height: 'auto',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    />
  );
}

function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 1;
  // RA grid (toutes les 30°)
  for (let ra = 0; ra <= 360; ra += 30) {
    const x = ((360 - ra) / 360) * w;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  // Dec grid (toutes les 30°)
  for (let dec = -60; dec <= 60; dec += 30) {
    const y = ((90 - dec) / 180) * h;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  // Labels axes
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '11px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('RA 0°', 4, h - 4);
  ctx.textAlign = 'right';
  ctx.fillText('RA 360°', w - 4, h - 4);
  ctx.fillText('Dec +90°', w - 4, 12);
  ctx.fillText('Dec -90°', w - 4, h - 16);
  ctx.textAlign = 'center';
  ctx.fillText('équateur céleste', w / 2, h / 2 - 4);
}

function drawLegend(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const lw = 140;
  const lh = 10;
  const x = w - lw - 12;
  const y = h - 36;
  // gradient
  const grad = ctx.createLinearGradient(x, y, x + lw, y);
  grad.addColorStop(0, 'rgba(120, 80, 255, 0.05)');
  grad.addColorStop(1, 'rgba(255, 160, 255, 1)');
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, lw, lh);
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.strokeRect(x, y, lw, lh);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '10px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('densité posterior', x, y - 4);
  ctx.fillText('faible', x, y + lh + 12);
  ctx.textAlign = 'right';
  ctx.fillText('forte', x + lw, y + lh + 12);
}
